import { errorIsStatus } from "@certn/api";
import {
  Avatar,
  ColumnDefinition,
  Container,
  CopyToClipboard,
  DataTable,
  MediaQuery,
  Pagination,
  SectionMessage,
  StatusTag,
  Text,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { CoApplicantsTooltip } from "./CoApplicantsTooltip";
import { GroupFilter } from "./GroupFilter";
import { LegacyAdjudicationStatusFilter } from "./LegacyAdjudicationStatusFilter";
import { LegacyCaseActionsMenu } from "./LegacyCaseActionsMenu";
import { LegacyChecksFilter } from "./LegacyChecksFilter";
import { LegacyReportStatusFilter } from "./LegacyReportStatusFilter";
import { LegacyScoreFilter } from "./LegacyScoreFilter";
import { LegacyStatusFilter } from "./LegacyStatusFilter";
import { NameTableCell } from "./NameTableCell";
import { RequestorFilter } from "./RequestorFilter";
import { TableEmptyState } from "./TableEmptyState";
import { FilterBar } from "../../../components/filters/FilterBar";
import { OrderTimeframeFilter } from "../../../components/order-timeframe-filter/OrderTimeframeFilter";
import { SortByDateFilter } from "../../../components/sort-by-date-filter/SortByDateFilter";
import { useQueryParams } from "../../../hooks/useQueryParams";

// Type definition for Legacy Case based on the API spec
type CoApplicant = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

type CoSigner = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

type ListingData = {
  unit?: string | null;
  address?: string | null;
};

type LegacyCase = {
  id: string;
  report_id: string;
  created: string;
  modified: string;
  applicant_first_name: string;
  applicant_last_name: string;
  applicant_email: string;
  status: string;
  report_status?: string;
  adjudication_status?: string;
  score: string | null;
  checks: string[];
  team_type: "HR" | "PM" | null;
  customer_group: {
    id: string;
    name: string;
  };
  customer_account: {
    id: string;
    auth0_display_name: string;
  };
  requested_by: {
    id: string;
    auth0_full_name: string;
  } | null;
  co_applicants?: CoApplicant[] | null;
  co_signers?: CoSigner[] | null;
  listing_data?: ListingData | null;
};

type PaginatedLegacyCaseList = {
  count: number;
  next: string | null;
  previous: string | null;
  results: LegacyCase[];
};

interface LegacyCasesTableProps {
  cases: PaginatedLegacyCaseList | undefined;
  isLoading: boolean;
  error: Error;
  teamType?: "HR" | "PM";
}

const renderStatusTag = (status: string) => {
  const statusLabels: Record<string, string> = {
    ACTION_REQUIRED: "Action required",
    CANCELLED: "Cancelled",
    COMPLETE: "Complete",
    IN_DISPUTE: "In dispute",
    IN_PROGRESS: "In progress",
    WAITING_ON_CANDIDATE: "Waiting on candidate",
    APPLICATION_EXPIRED: "Application expired",
  };

  switch (status) {
    case "ACTION_REQUIRED":
      return (
        <StatusTag
          leftIcon="emoji_flags"
          fill="background-accent-red-weak"
          color="text-accent-red-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "CANCELLED":
      return (
        <StatusTag
          leftIcon="block"
          fill="background-accent-red-weak"
          color="text-accent-red-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "COMPLETE":
      return (
        <StatusTag
          leftIcon="inventory"
          fill="background-accent-green-weak"
          color="text-accent-green-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "IN_DISPUTE":
      return (
        <StatusTag
          leftIcon="hourglass_top"
          fill="background-accent-purple-weak"
          color="text-accent-purple-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "IN_PROGRESS":
      return (
        <StatusTag
          leftIcon="document_scanner"
          fill="background-accent-tofino-weak"
          color="text-accent-tofino-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "WAITING_ON_CANDIDATE":
      return (
        <StatusTag
          leftIcon="pending"
          fill="background-accent-neutral-medium"
          color="text-base"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    case "APPLICATION_EXPIRED":
      return (
        <StatusTag
          leftIcon="block"
          fill="background-accent-red-weak"
          color="text-accent-red-strong"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status]}
        </StatusTag>
      );
    default:
      return (
        <StatusTag
          leftIcon="pending"
          fill="background-accent-neutral-medium"
          color="text-base"
          statusInfoIcon={false}
          testID="legacy-status-tag"
        >
          {statusLabels[status] || status}
        </StatusTag>
      );
  }
};

const renderScoreTag = (score: string | null) => {
  if (!score) return "--";

  switch (score) {
    case "PASS":
    case "CLEARED":
      return (
        <StatusTag
          leftIcon="inventory"
          fill="background-accent-blue-weak"
          color="text-accent-blue-strong"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          {score === "CLEARED" ? "Clear" : score}
        </StatusTag>
      );
    case "WARN":
    case "REVIEW":
      return (
        <StatusTag
          leftIcon="quick_reference_all"
          fill="background-accent-orange-weak"
          color="text-accent-orange-strong"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          {score}
        </StatusTag>
      );
    case "FAIL":
    case "DIDNOTPASS":
      return (
        <StatusTag
          leftIcon="block"
          fill="background-accent-red-weak"
          color="text-accent-red-strong"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          {score === "DIDNOTPASS" ? "Did Not Pass" : score}
        </StatusTag>
      );
    case "NA":
      return (
        <StatusTag
          leftIcon="pending"
          fill="background-accent-neutral-medium"
          color="text-base"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          N/A
        </StatusTag>
      );
    case "NONE":
      return (
        <StatusTag
          leftIcon="pending"
          fill="background-accent-neutral-medium"
          color="text-base"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          None
        </StatusTag>
      );
    default:
      return (
        <StatusTag
          leftIcon="pending"
          fill="background-accent-neutral-medium"
          color="text-base"
          statusInfoIcon={false}
          testID="legacy-score-tag"
        >
          {score}
        </StatusTag>
      );
  }
};

export function LegacyCasesTable({
  cases,
  isLoading,
  error,
  teamType,
}: LegacyCasesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { params, setParam } = useQueryParams();
  const isGroupOverview = pathname.includes("/group-overview");

  // Use all rows from API - team_type filtering should be handled by backend
  const rows = cases?.results || [];

  // Shared column definitions
  const nameColumn: ColumnDefinition<LegacyCase> = {
    key: "applicant_name",
    name: t("Name"),
    isRowHeader: true,
    render({ applicant_first_name, applicant_last_name, applicant_email }) {
      const nameObject =
        applicant_first_name || applicant_last_name
          ? {
              given_name: applicant_first_name || "",
              family_name: applicant_last_name || "",
            }
          : undefined;

      return (
        <NameTableCell
          name={nameObject}
          email={applicant_email}
          linkedCases={[]}
        />
      );
    },
  };

  const caseIdColumn: ColumnDefinition<LegacyCase> = {
    key: "report_id",
    name: t("Case ID"),
    width: 200,
    textAlignment: "left",
    render({ report_id }) {
      return <Text>{report_id}</Text>;
    },
  };

  const groupColumn: ColumnDefinition<LegacyCase> = {
    key: "customer_group",
    name: t("Group"),
    width: 150,
    render({ customer_group }) {
      return customer_group?.name || "--";
    },
  };

  const createdColumn: ColumnDefinition<LegacyCase> = {
    key: "created",
    name: t("Date created"),
    allowsSorting: true,
    width: 120,
    render({ created }) {
      return formatDate(created, "MMM dd, yyyy");
    },
    testID: "legacy-created-column-header",
  };

  const reportStatusColumn: ColumnDefinition<LegacyCase> = {
    key: "report_status",
    name: t("Report status"),
    width: 150,
    render({ report_status }) {
      return report_status ? renderStatusTag(report_status) : "--";
    },
  };

  const statusColumn: ColumnDefinition<LegacyCase> = {
    key: "status",
    name: t("Status"),
    width: 150,
    render({ status }) {
      return status ? renderStatusTag(status) : "--";
    },
  };

  const adjudicationStatusColumn: ColumnDefinition<LegacyCase> = {
    key: "adjudication_status",
    name: t("Adj. Status"),
    width: 150,
    render({ adjudication_status }) {
      return adjudication_status ? renderStatusTag(adjudication_status) : "--";
    },
  };

  const scoreColumn: ColumnDefinition<LegacyCase> = {
    key: "score",
    name: t("Score"),
    width: 120,
    render({ score }) {
      return renderScoreTag(score);
    },
  };

  const requestorColumn: ColumnDefinition<LegacyCase> = {
    key: "requested_by",
    name: t("Requestor"),
    width: 82,
    render({ requested_by }) {
      return requested_by ? (
        <Avatar name={parseName(requested_by.auth0_full_name || undefined)} />
      ) : (
        "--"
      );
    },
  };

  const actionsColumn: ColumnDefinition<LegacyCase> = {
    key: "actions",
    isAction: true,
    render(caseItem) {
      return (
        <LegacyCaseActionsMenu
          caseId={caseItem.id}
          reportId={caseItem.report_id}
          teamType={teamType || caseItem.team_type || "HR"}
        />
      );
    },
  };

  // Team-specific columns for PM
  const addressColumn: ColumnDefinition<LegacyCase> = {
    key: "address",
    name: t("Address"),
    width: 200,
    render({ listing_data }) {
      return listing_data?.address || "--";
    },
  };

  const unitColumn: ColumnDefinition<LegacyCase> = {
    key: "unit",
    name: t("Unit"),
    width: 100,
    render({ listing_data }) {
      return listing_data?.unit || "--";
    },
  };

  const coApplicantsColumn: ColumnDefinition<LegacyCase> = {
    key: "co_applicants",
    name: "",
    width: 50,
    render({ co_applicants, co_signers }) {
      return (
        <CoApplicantsTooltip
          coApplicants={co_applicants}
          coSigners={co_signers}
        />
      );
    },
  };

  // Build columns based on team type
  let columns: ColumnDefinition<LegacyCase>[] = [];

  if (teamType === "HR") {
    // HR columns: Name, Case ID, Group, Date created, Status, Adj. Status, Report status, Score, Requestor, Actions
    columns = [
      nameColumn,
      caseIdColumn,
      groupColumn,
      createdColumn,
      statusColumn,
      adjudicationStatusColumn,
      reportStatusColumn,
      scoreColumn,
      requestorColumn,
      actionsColumn,
    ];
  } else if (teamType === "PM") {
    // PM columns: Name, Case ID, Address, Unit, Group, Date created, Co-applicants, Status, Report status, Score, Actions
    columns = [
      nameColumn,
      caseIdColumn,
      addressColumn,
      unitColumn,
      groupColumn,
      createdColumn,
      coApplicantsColumn,
      statusColumn,
      reportStatusColumn,
      scoreColumn,
      actionsColumn,
    ];
  } else {
    // Default columns (when no teamType is specified)
    columns = [
      nameColumn,
      caseIdColumn,
      groupColumn,
      createdColumn,
      statusColumn,
      reportStatusColumn,
      scoreColumn,
      requestorColumn,
      actionsColumn,
    ];
  }

  // Hide group column if we're in group overview mode
  const filteredColumns = isGroupOverview
    ? columns.filter((col) => col.key !== "customer_group")
    : columns;

  const isSearching = !Object.values(params).every(
    (value) => value === undefined
  );

  return (
    <Container layout="full-width" testID="legacy-cases-tab-container">
      <FilterBar
        hasSearch
        clearFilterExceptions={
          isGroupOverview ? ["customer_group_id"] : undefined
        }
        testID="legacy-cases"
      >
        <MediaQuery type="show" sm md>
          <SortByDateFilter />
        </MediaQuery>
        {!isGroupOverview && <GroupFilter />}
        {teamType === "HR" ? (
          <>
            <LegacyStatusFilter />
            <LegacyAdjudicationStatusFilter />
            <LegacyReportStatusFilter />
            <LegacyScoreFilter />
            <LegacyChecksFilter />
            <MediaQuery type="hide" sm md>
              <OrderTimeframeFilter
                param="order_timeframe_after"
                showExtendedOptions
              />
            </MediaQuery>
            <RequestorFilter paramName="requested_by" />
          </>
        ) : teamType === "PM" ? (
          <>
            <LegacyStatusFilter />
            <LegacyReportStatusFilter />
            <LegacyScoreFilter />
            <LegacyChecksFilter />
            <MediaQuery type="hide" sm md>
              <OrderTimeframeFilter
                param="order_timeframe_after"
                showExtendedOptions
              />
            </MediaQuery>
            <RequestorFilter paramName="requested_by" />
          </>
        ) : (
          // Default filters when no team type is specified
          <>
            <LegacyStatusFilter />
            <LegacyReportStatusFilter />
            <LegacyScoreFilter />
            <LegacyChecksFilter />
            <MediaQuery type="hide" sm md>
              <OrderTimeframeFilter
                param="order_timeframe_after"
                showExtendedOptions
              />
            </MediaQuery>
            <RequestorFilter paramName="requested_by" />
          </>
        )}
      </FilterBar>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t("Unable to retrieve legacy cases")}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <MediaQuery type="hide" sm md>
        <DataTable
          aria-label={t("Legacy Cases Table")}
          columns={filteredColumns}
          rows={rows}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
          onRowAction={(id) => {
            // Find the clicked row to get the report_id
            const clickedCase = rows.find((row) => row.id === id);
            if (clickedCase) {
              navigate(`/legacy-case-details/${clickedCase.report_id}`, {
                state: {
                  last: { href: `${pathname}${search}` },
                },
              });
            }
          }}
          renderEmptyState={() => (
            <TableEmptyState isSearching={isSearching} statusSubset="legacy" />
          )}
          isLoading={isLoading}
        />
      </MediaQuery>
      <MediaQuery type="show" sm md>
        {rows.length ? (
          rows.map((item) => {
            const firstName = item.applicant_first_name;
            const lastName = item.applicant_last_name;

            return (
              <div
                key={item.report_id}
                className={styles.mobileCard}
                onClick={() => {
                  navigate(`/legacy-case-details/${item.report_id}`, {
                    state: {
                      last: { href: `${pathname}${search}` },
                    },
                  });
                }}
              >
                <div className={styles.mobileCardRow}>
                  <NameTableCell
                    name={
                      firstName || lastName
                        ? {
                            given_name: firstName || "",
                            family_name: lastName || "",
                          }
                        : undefined
                    }
                    email={item.applicant_email}
                    linkedCases={[]}
                    isMobile
                  />
                  {renderStatusTag(item.status)}
                </div>
                <div className={styles.mobileCardRow}>
                  <div>Score: {renderScoreTag(item.score)}</div>
                  <div>
                    ID:{" "}
                    <CopyToClipboard
                      text={item.report_id}
                      testID="copyable-mobile-legacy-case-id"
                    />
                  </div>
                </div>
                {teamType === "PM" && (
                  <div className={styles.mobileCardRow}>
                    <div>Address: {item.listing_data?.address || "--"}</div>
                    <div>Unit: {item.listing_data?.unit || "--"}</div>
                  </div>
                )}
                <div className={styles.mobileCardRow}>
                  <div>Created: {formatDate(item.created, "MMM dd, yyyy")}</div>
                  {!isGroupOverview && (
                    <div>Group: {item.customer_group?.name || "--"}</div>
                  )}
                </div>
                {teamType === "HR" && item.requested_by && (
                  <div className={styles.mobileCardRow}>
                    <div>Requestor: {item.requested_by.auth0_full_name}</div>
                  </div>
                )}
                {teamType === "PM" &&
                  (item.co_applicants?.length || item.co_signers?.length) && (
                    <div className={styles.mobileCardRow}>
                      <CoApplicantsTooltip
                        coApplicants={item.co_applicants}
                        coSigners={item.co_signers}
                      />
                    </div>
                  )}
              </div>
            );
          })
        ) : (
          <div className={styles.illustration}>
            <TableEmptyState isSearching={isSearching} statusSubset="legacy" />
          </div>
        )}
      </MediaQuery>
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={cases?.count || 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
}

const styles = {
  illustration: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }),
  mobileCard: css({
    border: "1px solid var(--color-border-base)",
    borderRadius: "var(--border-radius-3)",
    padding: "var(--space-3)",
    marginBottom: "var(--space-2)",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "var(--color-surface-hover)",
    },
  }),
  mobileCardRow: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "var(--space-2)",
    "&:last-child": {
      marginBottom: 0,
    },
  }),
};
